import { useEffect, useState } from 'react';

/**
 * Hook for filtering and sorting list
 * @param list List to be filtered and sorted. Type of list item must have name property
 * @param additionalFilter Optional additional filter predicate function
 * @returns Filtered and sorted list, setFilterFunction
 */
export const useFilteredAndSorted = <T extends { name: string }>(
  list: T[],
  additionalFilter?: (listItem: T) => boolean
): [T[], React.Dispatch<React.SetStateAction<string>>] => {
  const [filter, setFilter] = useState<string>('');
  const [filteredAndSortedList, setFilteredAndSortedList] = useState<T[]>(
    filterAndSort(list, filter, additionalFilter)
  );
  useEffect(
    () => setFilteredAndSortedList(filterAndSort(list, filter, additionalFilter)),
    [list, filter]
  );

  return [filteredAndSortedList, setFilter];
};

const filterAndSort = <T extends { name: string }>(
  list: T[],
  filter: string,
  additionalFilter?: (listItem: T) => boolean
) =>
  list
    .filter((listItem) => textIncludesFilter(listItem.name, filter))
    .filter((listItem) => (additionalFilter ? additionalFilter(listItem) : true))
    .sort((a, b) => alphabeticCompare(a.name, b.name));

/**
 * Checks whether a string 'text' includes a substring equal to 'filter'
 * @param text String to be filtered
 * @param filter String which is the filter condition
 * @returns True if filter is a substring within text
 */
const textIncludesFilter = (text: string, filter: string): boolean =>
  filter === '' || text.toLowerCase().includes(filter.toLowerCase());

/**
 * Comparator to be used in string.sort() function
 * Sorts alphabetically
 * @param string1 string to compare
 * @param string2 string to be compared to
 * @returns a positive or negative number based on the result of the comparison
 */
const alphabeticCompare = (string1: string, string2: string): number => {
  string1 = string1.toLowerCase();
  string2 = string2.toLowerCase();
  return string1 < string2 ? -1 : string1 > string2 ? 1 : 0;
};
